import { shrinkImage, shrinkOpts } from "./imageShrink";
import { enqueueOriginal } from "./uploadQueue";

const CHUNK = 2 * 1024 * 1024;          // 2MB/片：弱网单片耗时短、失败重传代价小
const MAX_TRIES = 6;                     // 每片最多 6 次（1s → 2s → 4s → 8s → 15s）
const KEY_PREFIX = "ali.up:";
const RESUME_TTL = 48 * 3600 * 1000;
export const UPLOAD_MAX = 200 * 1024 * 1024;

// 「等待网络恢复」自动续传（2026-10-24，老板拍板做）：
// 背景：Jerry 经梯子上传反复 Network Error——请求在网络层就没到服务器，重试的
// 6 次（约 1 分钟）全部落在断流窗口里就整体判死，用户得手动点重试。而他的断流
// 是分钟级的，等一等就能蹭到恢复的间隙；且 navigator.onLine 在梯子假死时仍是
// true，现有的 waitOnline 探测不到，必须主动向服务端发探测请求。
// 做法：重试耗尽后不再立刻失败，转入「已离线 · 恢复网络后自动继续」（复用现有
// offline 状态文案），每 PROBE_INTERVAL_MS 用 GET /uploads/status 轻探测一次
//（2xx = 链路通，顺带校验分片会话仍在），通了就重置重试计数接着传（断点续传
// 会跳过服务端已收的分片）。WAIT_SERVER_BACK_MS 是每份文件的总等待预算——防止
// 服务端对该文件持续报错时无限空转；预算耗尽才按原错误判死。用户「停止」
//（signal abort）与 401/400 这类非链路错误会在探测循环里立即退出。
const WAIT_SERVER_BACK_MS = 10 * 60 * 1000;
const PROBE_INTERVAL_MS = 5000;

// 最终文件校验码：整文件 SHA-256（≤128MB，超出则跳过以免一次性读入内存）
const CHECKSUM_MAX = 128 * 1024 * 1024;
const sha256Hex = async (file) => {
  try {
    if (!file || file.size > CHECKSUM_MAX || typeof crypto === "undefined" || !crypto.subtle) return null;
    const buf = await file.arrayBuffer();
    const dig = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(dig)).map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch { return null; }
};

const sleep = (ms, signal) => new Promise((res, rej) => {
  const t = setTimeout(res, ms);
  signal?.addEventListener("abort", () => { clearTimeout(t); rej(abortErr()); }, { once: true });
});
const abortErr = () => { const e = new Error("canceled"); e.code = "ERR_CANCELED"; e.name = "CanceledError"; return e; };
export const isCanceled = (e) => e?.code === "ERR_CANCELED" || e?.name === "CanceledError" || e?.name === "AbortError";
const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
const fileKey = (f) => `${KEY_PREFIX}${f.name}:${f.size}:${f.lastModified}`;

const restore = (f) => {
  try {
    const raw = localStorage.getItem(fileKey(f));
    if (!raw) return null;
    const { id, at } = JSON.parse(raw);
    if (Date.now() - at > RESUME_TTL) { localStorage.removeItem(fileKey(f)); return null; }
    return id;
  } catch { return null; }
};
const remember = (f, id) => {
  try {
    const now = Date.now();
    Object.keys(localStorage).filter((k) => k.startsWith(KEY_PREFIX)).forEach((k) => { try { if (now - JSON.parse(localStorage.getItem(k)).at > RESUME_TTL) localStorage.removeItem(k); } catch { localStorage.removeItem(k); } });
    localStorage.setItem(fileKey(f), JSON.stringify({ id, at: now }));
  } catch { /* 隐私模式等：不影响上传，只是不能跨刷新续传 */ }
};
const forget = (f) => { try { localStorage.removeItem(fileKey(f)); } catch { /* */ } };

// 断网时挂起，等 online 事件再继续
const waitOnline = (signal, onState) => new Promise((res, rej) => {
  if (typeof navigator === "undefined" || navigator.onLine !== false) return res();
  onState?.("offline");
  const ok = () => { window.removeEventListener("online", ok); res(); };
  window.addEventListener("online", ok);
  signal?.addEventListener("abort", () => { window.removeEventListener("online", ok); rej(abortErr()); }, { once: true });
});

// 不可重试：4xx（除 408 / 409 / 429）；可重试：网络错误 / 5xx / 超时
const retryable = (e) => { const st = e?.response?.status; return !st || st >= 500 || st === 408 || st === 429; };

export const uploadErrText = (e, zh = true) => {
  if (isCanceled(e)) return zh ? "已取消上传" : "Upload canceled";
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object") return d.message || (zh ? "上传失败" : "Upload failed");
  if (d) return d;
  const status = e?.response?.status;
  if (!status || e?.message === "Network Error") {
    return zh
      ? "网络连接中断，已自动重试仍未恢复；请检查网络后重试"
      : "The connection was interrupted and automatic retries did not recover. Check your network and retry.";
  }
  if (status >= 500) {
    return zh
      ? `服务器暂时不可用（${status}），请稍后重试`
      : `The server is temporarily unavailable (${status}). Try again later.`;
  }
  return e?.message || (zh ? "上传失败" : "Upload failed");
};

/** Append the metadata expected by the design file-registration endpoints. */
export const appendUploadRef = (fd, uploaded, sourceFile) => {
  const filename = uploaded?.filename || sourceFile?.name || "file";
  fd.append("fileId", uploaded?.fileId || "");
  fd.append("fileName", filename);
  fd.append("fileSize", String(uploaded?.size ?? sourceFile?.size ?? 0));
  fd.append("contentType", uploaded?.contentType || sourceFile?.type || "application/octet-stream");
};

/**
 * 分片上传 + 自动重试 + 断点续传 → { fileId, filename, contentType, size, kind }
 *  onProgress({ pct, loaded, total, state })  state: uploading | retrying | offline | finalizing
 *  signal: AbortController.signal 取消
 *
 *  opts.shrink —— 上传前的前端图片压缩（2026-10-15 新增，全局生效）：
 *    · 不传（默认）→ 用 IMG_PRESETS.material（长边 3840 / q0.8 / WebP）压大图；
 *    · 传 "shot"    → 截图档（长边 2880 ≈ 230KB），需求反馈 / 聊天发图用；
 *    · 传对象       → 自定义 { maxSide, quality, minBytes, format }；
 *    · 传 false     → 不压（已自己压过、或明确要原图的调用方）。
 *    **放在这里而不是各调用方**：全站上传只有这一个入口，改一处所有模块（聊天 / 项目 /
 *    设计 / 协作 / 反馈）自动都有；而且治的是「上传慢」—— 传的是几百 KB 而不是 10MB。
 *    非图片（视频 / PDF / 表格 / 压缩包）shrinkImage 会原样返回，无副作用。
 *    压缩失败 / 解码失败（如 Chrome 遇 HEIC）一律原样上传，绝不阻断。
 *
 *  ⚠️ 压缩改变了文件时，**name / type 必须以压缩后的文件为准**（2026-10-20 加）：
 *    压缩现在输出 WebP，但不少调用方会显式传 `{ name: "x.jpg", type: "image/jpeg" }`
 *    （SmsComposer、ProjectContacts 等）。若继续用调用方给的名字和 MIME，就会出现
 *    「内容说是 WebP、头里写着 image/jpeg」的错位 —— 服务端按 MIME 记账，返回时
 *    也照 MIME 回 Content-Type，Safari 这类不嗅探的浏览器直接渲染失败（破图）。
 *    所以仅在**文件对象变了**（真压过）时才用新文件的 name/type；没压过（返回同一个
 *    对象，如视频 / 小图 / 压不动）仍尊重调用方传的值。
 *    另外（同一个坑的另一种走法）：有些调用方先自己调 `shrinkImage` 再把结果传进来，
 *    此时 `file` 已经是 WebP，但调用方手里的 `type` 仍是**用户原始选的格式**
 *    （iPhone 的 HEIC 就是这样）。所以只要是图片，就信 `file.type` —— 它要么是
 *    shrinkImage 写好的正确值，要么就是原文件本身的类型，永远比外部的 type 准。
 *
 *  opts.keepOriginal —— 后台补传源文件（2026-10-20 新增，用户需求）：
 *    需求原话：「有没有加快上传速度，并且能将源文件也上传的办法…下载时下载源文件」。
 *
 *    做法：**先传压缩预览**（几百 KB，秒传，页面立刻能预览 / 提交 / 走审批），
 *    原图在后台继续分片上传，传完由 `/files/{fid}/original` 挂到预览文件上。
 *    之后用户点下载走 `?src=1` 拿到原图，页面内预览仍用轻量版。
 *
 *    为什么这样是唯一能同时满足两点的做法：总字节数不可能变少（原图就是 6MB，
 *    想保留它就得传 6MB）。能省的是**等待**——把"等 6MB 传完才能继续"变成
 *    "等 1MB 就能继续，剩下的在后台跑"。所以这里刻意让补传**不阻断、不报错**：
 *    失败了用户手上的预览图照样能用，只是这单没有源文件。
 *
 *    取舍：存储会多一份原图。只有明确需要源文件的入口（现场照片、设计交付物）
 *    才开这个开关，聊天发图、头像等维持现状不再多存。
 *
 *     ⚠️ **不要在调用方先自己压一遍再传进来**（2026-10-20 实测踩坑，现场照片就是
 *    这么写坏的）：本函数把**入参当作原图**（下面 `const original = file`）。若调用方
 *    传入的已经是压缩结果，`original !== file` 恒为 false → 源图永远不入队、下载也
 *    拿不到原图，而界面一切正常，极难发现。正确做法：把用户选的原始 File 直接交进来，
 *    压缩交给这里的 `shrink` 选项统一处理。确实必须自己预处理时，用 `originalFile`
 *    显式把真原图带进来。
 *
 *  opts.originalFile —— 显式指定「真正的原图」（可选，默认就是入参 file）。
 *    存在的意义就是上面那条防呆：调用方若已自行压缩，把真正的原始 File 传进来，
 *    补传队列用的就是它，而不是压缩结果。
 *
 *  opts.onStage —— 上传阶段回调（可选，目前只给后台补传 worker 用）。
 *    后台补传是**看不见**的：没有进度条、没有界面。线上出现「原图一直没补上」时
 *    只能靠猜。worker 传入这个回调，把「当前进行到哪一步」（续传查询 / 第 N 片 /
 *    校验 / 合并）写进 `window.__uploadRunner.lastStage`，用户敲一下就能定位。
 *    普通前台调用不传，完全无额外开销。
 */
export async function uploadChunked(api, file, { name, type, onPct, onProgress, signal, shrink, keepOriginal, onOriginal, originalFile, skipCompress, onStage } = {}) {
  const dbg = typeof onStage === "function" ? onStage : () => {};
  dbg("enter");
  // 原图 = 调用方显式给的那份；没给就是入参本身（此时入参必须还是未压缩的原始文件）
  const original = originalFile || file;
  if (shrink !== false && file) {
    try { file = (await shrinkImage(file, shrinkOpts(shrink))) || file; } catch { /* 压缩失败 → 原图上传 */ }
  }
  const shrunk = file !== original;
  const isImage = (file?.type || "").startsWith("image/");
  const filename = (shrunk ? null : name) || file?.name || name || "upload.bin";
  const contentType = isImage
    ? file.type
    : ((shrunk ? null : type) || file?.type || type || "application/octet-stream");
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  const expected = (i) => (file.size === 0 ? 0 : Math.min(CHUNK, file.size - i * CHUNK));
  const report = (loaded, state = "uploading") => {
    const pct = file.size ? Math.min(100, Math.round((loaded / file.size) * 100)) : 100;
    onPct?.(pct);
    onProgress?.({ pct, loaded, total: file.size, state });
  };
  const fin = await sendAll({ api, file, filename, contentType, expected, total, report, signal, skipCompress, dbg });
  dbg("sent");
  // 原图入库排队（见 opts.keepOriginal 说明）
  if (keepOriginal && original && original !== file && original.size > file.size) {
    // 写进 IndexedDB 而不是直接发请求：这样**离开页面 / 刷新后仍能续传**
    // （由 lib/uploadRunner.js 的全局 worker 取出执行）。代价是这一份原图会在
    // 浏览器本地临时占用 original.size 的空间，传完即删。
    try {
      await enqueueOriginal({
        previewFileId: fin.fileId,
        blob: original,
        filename: original.name || filename,
        contentType: original.type || "application/octet-stream",
        lastModified: original.lastModified,
        // 记下是谁的原图：worker 只补传当前登录账号的任务（见 uploadQueue.peekOriginal）
        username: (() => { try { return JSON.parse(localStorage.getItem("ali_user") || "{}").username || ""; } catch { return ""; } })(),
      });
      try { onOriginal?.({ status: "queued", size: original.size }); } catch { /* */ }
    } catch (e) {
      // IndexedDB 不可用（极老的浏览器 / 隐私模式下被禁）→ 退回"当场传完"
      console.warn("[upload] 原图入队失败，改为立即补传", e?.message || e);
      sendAll({
        api, file: original, filename: original.name || filename,
        contentType: original.type || "application/octet-stream",
        expected: (i) => Math.min(CHUNK, original.size - i * CHUNK),
        total: Math.max(1, Math.ceil(original.size / CHUNK)),
        report: () => {}, signal: undefined, skipCompress: true,
      })
        .then((ofin) => api.post(`/files/${fin.fileId}/original`, { originalFileId: ofin.fileId }))
        .then(() => { try { onOriginal?.({ status: "done" }); } catch { /* */ } })
        .catch(() => { try { onOriginal?.({ status: "failed" }); } catch { /* */ } });
    }
  }
  return fin;
}

/**
 * 分片上传的「整包」实现：断点续传 → 逐片上传 → 校验 → finalize。
 * 抽出来是为了给「后台补传源文件」复用（原图与预览图各走一遍同样的可靠链路）。
 *
 * skipCompress：只给「补传源文件」用 —— 源文件的用途就是原样保留，服务端不要再压一遍
 * （它由调用方在 finalize 时带上，服务端据此跳过压缩，见 server.py 的 /uploads/finalize）。
 */
async function sendAll({ api, file, filename, contentType, expected, total, report, signal, skipCompress, dbg }) {
  const mark = typeof dbg === "function" ? dbg : () => {};
  mark("sendAll:enter");
  // 续传：同一文件（名 + 大小 + 修改时间）在 48h 内重选 / 刷新后，沿用 uploadId 并跳过服务端已收分片
  let uploadId = restore(file);
  let received = {};
  if (uploadId) {
    mark("sendAll:status");
    try {
      const { data } = await api.get("/uploads/status", { params: { uploadId }, signal });
      if (data.exists) received = data.received || {}; else uploadId = null;
    } catch (e) { if (isCanceled(e)) throw e; uploadId = null; }
  }
  if (!uploadId) { uploadId = newId(); remember(file, uploadId); }
  mark("sendAll:resume-ready");

  // 「等待网络恢复」预算：按探测轮次计（每轮 = 一次探测 + 间隔），分片与 finalize 共享。
  // 用轮次而不是纯墙钟：若服务端对该文件持续报错（探测通、重试仍败），墙钟可能永远
  // 扣不满导致无限空转；轮次封顶一定收敛。恢复健康后正常传完的文件不消耗轮次。
  let waitRounds = 30;
  const waitServerBack = async () => {
    while (waitRounds > 0) {
      if (signal?.aborted) throw abortErr();
      waitRounds -= 1;
      mark("sendAll:probe");
      try {
        // 探测复用 /uploads/status：轻量、且响应本身能确认分片会话还在（72h 内）
        await api.get("/uploads/status", { params: { uploadId }, timeout: 8000, signal });
        return true;
      } catch (e) {
        if (signal?.aborted) throw abortErr();
        const st = e?.response?.status;
        // 401/400/404 这类「链路通了但业务失败」不要干等：401 交给全局拦截器跳登录，
        // 400/404 说明会话真的没了，等下去只会空转。
        if (st && st !== 408 && st !== 429 && st < 500) throw e;
        await sleep(PROBE_INTERVAL_MS, signal);
      }
    }
    return false;
  };

  let done = 0;
  const sendChunk = async (i) => {
    for (let t = 0; ; t++) {
      if (signal?.aborted) throw abortErr();
      mark(`sendChunk:${i}:waitOnline`);
      await waitOnline(signal, (s) => report(done, s));
      mark(`sendChunk:${i}:post`);
      // 停滞看门狗（2026-10-24，现场同事 Mac Chrome 分片上传反复 Network Error）：
      // DevTools 实测形态是「chunk POST 带完整包体发出、拖 20~140 秒拿不到任何响应」
      // （VPN/代理链路把 QUIC/大包整条掐死，而 navigator.onLine 仍为 true，
      // waitOnline 探测不到；线上 Alt-Svc 通告了 h3，Chrome 优先走 QUIC）。
      // 原来只靠 120s 总超时兜底，一次假死要干等两分钟才轮到重试，窗口早错过了。
      // 改为「20 秒零字节进展」即掐掉本次尝试立刻重试——只看进展不看总时长，
      // 慢网络（几百 kbps 传 2MB 需要几十秒但字节一直在走）不会被误伤；
      // 断流间隙恢复时能在几十秒内蹭到好窗口，而不是 2 分钟一发子弹。
      const attempt = new AbortController();
      const onOuterAbort = () => attempt.abort(signal?.reason);
      signal?.addEventListener("abort", onOuterAbort, { once: true });
      let lastLoaded = 0, lastTs = Date.now();
      const watchdog = setInterval(() => { if (Date.now() - lastTs > 20000) attempt.abort(new Error("upload-stalled")); }, 4000);
      let caught = null;
      try {
        const fd = new FormData();
        fd.append("uploadId", uploadId);
        fd.append("index", i);
        fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), filename);
        await api.post("/uploads/chunk", fd, { headers: { "Content-Type": "multipart/form-data" }, signal: attempt.signal, timeout: 120000,
          onUploadProgress: (ev) => {
            const loaded = ev?.loaded || 0;
            if (loaded > lastLoaded) { lastLoaded = loaded; lastTs = Date.now(); }
            report(done + Math.min(loaded, expected(i)));
          } });
        mark(`sendChunk:${i}:ok`);
        return;
      } catch (e) {
        mark(`sendChunk:${i}:err:${String(e?.message || e).slice(0, 60)}`);
        // 外层 signal 触发（用户点「停止」/离开取消）→ 原样按取消上抛；
        // 看门狗掐掉的假死尝试对 axios 同样表现为 ERR_CANCELED，但外层没 abort，
        // 此时要走下面的可重试分支，绝不能当成用户取消吞掉。
        if (signal?.aborted) throw abortErr();
        caught = e;
      } finally {
        clearInterval(watchdog);
        signal?.removeEventListener("abort", onOuterAbort);
      }
      // 重试/等待的处理放在 finally 之后：等待网络恢复可能长达数分钟，
      // 不能让看门狗的 interval 在这段期间一直空转。
      if (!retryable(caught)) throw caught;
      if (t + 1 >= MAX_TRIES) {
        // 6 次重试全部落在断流窗口 → 不判死，转入「已离线 · 恢复网络后自动继续」：
        // 探测到链路恢复就重置重试计数接着传（断点续传会跳过服务端已收的分片）；
        // 探测预算耗尽才按原错误判死（界面显示与今天相同的网络中断文案）。
        mark(`sendChunk:${i}:wait-server`);
        report(done, "offline");
        if (!(await waitServerBack())) throw caught;
        report(done, "retrying");
        t = -1;
        continue;
      }
      report(done, "retrying");
      await sleep(Math.min(15000, 1000 * 2 ** t), signal);
    }
  };

  for (let i = 0; i < total; i++) {
    if (received[i] === expected(i)) { done += expected(i); report(done); continue; }
    mark(`sendAll:chunk:${i}`);
    await sendChunk(i);
    done += expected(i);
    report(done);
  }

  // 最终文件校验码：合并后由服务端逐字节比对，损坏即拒收
  report(file.size, "hashing");
  mark("sendAll:hashing");
  const checksum = await sha256Hex(file);
  mark("sendAll:hashed");

  // 合并：服务端校验缺片 → 409 {missing} 只补缺的；503（存储抖动）/ 网络错误 → 退避重试
  for (let round = 0, tries = 0; ; ) {
    if (signal?.aborted) throw abortErr();
    report(file.size, "finalizing");
    mark("sendAll:finalize");
    try {
      const { data } = await api.post("/uploads/finalize", { uploadId, filename, contentType, size: file.size, chunkSize: CHUNK, totalChunks: total, checksum, skipCompress: !!skipCompress }, { signal, timeout: 900000 });
      forget(file);
      return data;
    } catch (e) {
      if (isCanceled(e)) throw e;
      const st = e?.response?.status;
      const detail = e?.response?.data?.detailRaw ?? e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.checksumMismatch) {
        forget(file);   // 校验码不一致：清掉续传记录，下次整包重传
        const err = new Error(detail.message || "文件校验码不一致，请重新上传");
        err.checksumMismatch = true;
        throw err;
      }
      const missing = st === 409 ? detail?.missing : null;
      if (Array.isArray(missing) && missing.length && round < 3) {
        round += 1;
        for (const i of missing) await sendChunk(i);
        continue;
      }
      if (st === 400) forget(file);   // 分片会话已不存在 / 文件过大：下次从头来
      if (retryable(e)) {
        if (tries < 3) { tries += 1; await waitOnline(signal); await sleep(1500 * tries, signal); continue; }
        // 快速重试耗尽 → 与分片同口径：转入「等待网络恢复」，恢复后重新给满快速
        // 重试。合并可安全重复调用：409 只补缺片，503（存储抖动）本来就是
        // 「稍后自动重试」语义；探测预算耗尽才按原错误判死。
        mark("sendAll:finalize-wait-server");
        report(file.size, "offline");
        if (await waitServerBack()) { tries = 0; continue; }
      }
      throw e;
    }
  }
}
